/**
 * THE ANNOUNCER — overlay component (design system, FOUNDATION — v3; S-22).
 *
 * The over-caffeinated esports caster surface: killstreak tier stings,
 * berserker start/end, streak-broken, pity condolences. Fire-and-forget
 * cosmetics — a missed overlay is no loss (screens.md S-22).
 *
 * v4 "a character, now a friendly one": the cracked-heart mark on every
 * plate, a warm serif sting under a soft amber glow, a gentle drop-in with a
 * small bounce, a one-shot screen-edge warmth, and an optional combo meter
 * that ticks up after the entrance. No screen shake — a celebratory nudge,
 * not an arcade slam (DESIGN-BRIEF v4 §4).
 *
 * Honors, in order (PRD §10 accessibility):
 *  - announcer volume "off"      → nothing renders, ever
 *  - volume "text"               → toast instead of overlay
 *  - prefers-reduced-motion      → toast instead of overlay (even on "full")
 *  - volume "full"               → the animated overlay sting
 *
 * Usage: announce({ sting: resolveCopy("announcer.tiers.5") ?? "", motif: "gold" })
 *        announce({ sting: "RAMPAGE", motif: "gold", combo: 5 })
 */
import { AnimatePresence, motion } from "framer-motion";
import { createStore, uiStore, useStore } from "../lib/store.js";
import { PixelHeartCracked } from "./pixel.js";
import { announcerVariants, prefersReducedMotion } from "./motion.js";
import { toast } from "./toast.js";

export interface AnnouncerSting {
  id: number;
  /** ALL-CAPS sting line (©announcer.*) — ≤8 words per the tone rules. */
  sting: string;
  /** Optional second line (kept lowercase, dim). */
  sub?: string;
  /** Visual motif: killstreak gold (default), berserker fire, super-reject
   *  violet, or brand accent (reject-match moments). */
  motif?: "gold" | "berserk" | "sr" | "accent";
  /** v3 — optional combo meter: how many ticks light up (capped at 5). */
  combo?: number;
  durationMs?: number;
}

const announcerStore = createStore<{ current: AnnouncerSting | null }>({ current: null });
let nextId = 1;
let timer: number | undefined;

export function announce(input: Omit<AnnouncerSting, "id">): void {
  const volume = uiStore.get().announcerVolume;
  if (volume === "off") return;
  const text = input.sub ? `${input.sting} — ${input.sub}` : input.sting;
  if (volume === "text" || prefersReducedMotion()) {
    toast(text, "announcer");
    return;
  }
  const sting: AnnouncerSting = { id: nextId++, ...input };
  announcerStore.set({ current: sting });
  window.clearTimeout(timer);
  timer = window.setTimeout(() => dismissAnnouncer(sting.id), input.durationMs ?? 3600);
}

export function dismissAnnouncer(id?: number): void {
  const current = announcerStore.get().current;
  if (current && (id === undefined || current.id === id)) {
    announcerStore.set({ current: null });
  }
}

const COMBO_TICKS = 5;

/** Mounted once by the app shell (a global HUD layer, not a route — S-22). */
export function AnnouncerHost() {
  const current = useStore(announcerStore, (s) => s.current);
  const motif = current?.motif ?? "gold";
  const lit = Math.max(0, Math.min(current?.combo ?? 0, COMBO_TICKS));
  return (
    <>
      {/* one-shot screen-edge flash — CSS animates to opacity 0 and stays */}
      {current && (
        <div
          key={`flash-${current.id}`}
          className={`announcer-flash announcer-flash--${motif}`}
          aria-hidden="true"
        />
      )}
      <AnimatePresence>
      {current && (
        <motion.div
          key={current.id}
          className={`announcer${motif !== "gold" ? ` announcer--${motif}` : ""}`}
          role="status"
          aria-live="polite"
          variants={announcerVariants}
          initial="initial"
          animate="enter"
          exit="exit"
          onClick={() => dismissAnnouncer()}
        >
          <span className="announcer__mark" aria-hidden="true">
            <PixelHeartCracked size={18} />
          </span>
          <div className="announcer__sting">{current.sting}</div>
          {current.sub && <div className="announcer__sub">{current.sub}</div>}
          {lit > 0 && (
            <div className="announcer__combo" aria-hidden="true">
              {Array.from({ length: COMBO_TICKS }, (_, i) => (
                <span
                  key={i}
                  className={`announcer__tick${i < lit ? " announcer__tick--lit" : ""}`}
                  style={i < lit ? { animationDelay: `${0.25 + i * 0.09}s` } : undefined}
                />
              ))}
            </div>
          )}
        </motion.div>
      )}
      </AnimatePresence>
    </>
  );
}
